import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Printer } from "lucide-react";
import { format } from "date-fns";
import { buildPaystackCallbackUrl } from "@/lib/payments";

function money(currency: string, amount: number) {
  return `${currency} ${amount.toFixed(2)}`;
}

function computeAdjustmentAmount(a: any, subtotal: number) {
  const mode = a.mode || "fixed";
  const type = a.type;
  const included = Boolean(a.included_in_prices);
  const fixed = Number(a.amount || 0);
  const rate = Number(a.percent_rate || 0);

  if (mode === "percent") {
    if (type === "tax" && included) {
      return rate > 0 ? subtotal * (rate / (100 + rate)) : 0;
    }
    return subtotal * (rate / 100);
  }

  return fixed;
}

export default function InvoicePreview() {
  const { id } = useParams();
  const sb = supabase as any;

  const [loading, setLoading] = useState(true);
  const [inv, setInv] = useState<any>(null);
  const [items, setItems] = useState<any[]>([]);
  const [adjustments, setAdjustments] = useState<any[]>([]);
  const [biz, setBiz] = useState<any>(null);
  const [payerEmail, setPayerEmail] = useState("");
  const [paying, setPaying] = useState(false);

  useEffect(() => {
    const run = async () => {
      if (!id) return;
      setLoading(true);
      const { data: invoice } = await sb
        .from("invoices")
        .select("*, business:businesses(id,name,phone,slug)")
        .eq("id", id)
        .single();
      const { data: items } = await sb.from("invoice_items").select("*").eq("invoice_id", id).order("position", { ascending: true });
      const { data: adjs } = await sb.from("invoice_adjustments").select("*").eq("invoice_id", id).order("position", { ascending: true });
      setInv(invoice);
      setBiz((invoice as any)?.business || null);
      setItems(items || []);
      setAdjustments(adjs || []);
      setPayerEmail(String((invoice as any)?.bill_to_email || ""));
      setLoading(false);
    };
    run();
  }, [id]);

  const subtotal = useMemo(() => items.reduce((sum, it) => sum + Number(it.quantity || 0) * Number(it.unit_price || 0), 0), [items]);
  const taxes = useMemo(
    () =>
      (adjustments || [])
        .filter((a: any) => a.type === "tax" && !a.included_in_prices)
        .reduce((s: number, a: any) => s + computeAdjustmentAmount(a, subtotal), 0),
    [adjustments, subtotal],
  );
  const discounts = useMemo(
    () => (adjustments || []).filter((a: any) => a.type === "discount").reduce((s: number, a: any) => s + computeAdjustmentAmount(a, subtotal), 0),
    [adjustments, subtotal],
  );
  const total = subtotal + taxes - discounts;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!inv) {
    return (
      <div className="min-h-screen bg-background p-6">
        <p className="text-sm text-muted-foreground">Invoice not found.</p>
      </div>
    );
  }

  const fromName = inv.bill_from_name || biz?.name || "—";
  const fromEmail = inv.bill_from_email || null;
  const fromPhone = inv.bill_from_phone || biz?.phone || null;
  const canPay = String(inv.status || "") !== "paid" && String(inv.status || "") !== "void";
  const businessSlug = (biz as any)?.slug || null;
  const publicCode = inv.public_code || null;

  return (
    <div className="min-h-screen bg-background p-4">
      <div className="mx-auto max-w-3xl">
        <div className="flex items-center justify-between gap-3 mb-4 print:hidden">
          <Button variant="outline" onClick={() => window.close()}>
            Close
          </Button>
          <Button onClick={() => window.print()}>
            <Printer className="mr-2 h-4 w-4" />
            Print / Save PDF
          </Button>
        </div>

        {canPay && businessSlug && publicCode ? (
          <div className="mb-4 print:hidden rounded-2xl border border-border bg-accent/30 p-4">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <p className="text-sm font-semibold text-foreground">Pay this invoice</p>
                <p className="text-xs text-muted-foreground">
                  You'll be redirected to Paystack Checkout. After payment, you'll return to this invoice.
                </p>
              </div>
              <div className="text-sm font-semibold text-foreground">{money(inv.currency, total)}</div>
            </div>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <div className="space-y-1">
                <Label className="text-xs">Email</Label>
                <Input
                  value={payerEmail}
                  onChange={(e) => setPayerEmail(e.target.value)}
                  placeholder="you@example.com"
                  type="email"
                />
              </div>
              <div className="flex items-end">
                <Button
                  className="w-full"
                  disabled={paying || !payerEmail.trim()}
                  onClick={async () => {
                    if (!payerEmail.trim()) return;
                    setPaying(true);
                    try {
                      const redirectPath = `/app/invoicing/preview/${id}`;
                      const callbackUrl = buildPaystackCallbackUrl(redirectPath);
                      const { data, error } = await supabase.functions.invoke("paystack-init", {
                        body: {
                          context: { type: "invoice_payment", invoiceId: String(inv.id) },
                          payerEmail: payerEmail.trim(),
                          callbackUrl,
                        },
                      });
                      if (error) throw error;
                      const url = String((data as any)?.authorizationUrl || "");
                      if (!url) throw new Error("Missing Paystack authorizationUrl");
                      
                      // Safari mobile fix: Use assign() with a small delay to prevent blocking
                      setTimeout(() => {
                        window.location.assign(url);
                      }, 50);
                    } catch (e: any) {
                      alert(e?.message || "Failed to start payment.");
                    } finally {
                      setPaying(false);
                    }
                  }}
                >
                  {paying ? "Redirecting…" : "Pay now"}
                </Button>
              </div>
            </div>
          </div>
        ) : null}

        <div className="rounded-2xl border border-border bg-card p-6 print:border-0 print:shadow-none">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-sm text-muted-foreground">Invoice</p>
              <p className="font-mono text-lg font-semibold text-foreground">{inv.invoice_number}</p>
            </div>
            <div className="text-right text-sm">
              <div className="text-muted-foreground">Issue date</div>
              <div className="text-foreground">{inv.issue_date}</div>
              {inv.due_date ? (
                <>
                  <div className="mt-2 text-muted-foreground">Due date</div>
                  <div className="text-foreground">{inv.due_date}</div>
                </>
              ) : null}
            </div>
          </div>

          <div className="mt-6 grid sm:grid-cols-2 gap-6">
            <div>
              <p className="text-xs uppercase text-muted-foreground">Bill to</p>
              <p className="font-semibold text-foreground mt-1">{inv.bill_to_name}</p>
              {inv.bill_to_email ? <p className="text-sm text-muted-foreground">{inv.bill_to_email}</p> : null}
              {inv.bill_to_phone ? <p className="text-sm text-muted-foreground">{inv.bill_to_phone}</p> : null}
            </div>
            <div>
              <p className="text-xs uppercase text-muted-foreground">From</p>
              <p className="font-semibold text-foreground mt-1">{fromName}</p>
              {fromEmail ? <p className="text-sm text-muted-foreground">{fromEmail}</p> : null}
              {fromPhone ? <p className="text-sm text-muted-foreground">{fromPhone}</p> : null}
            </div>
          </div>

          <div className="mt-6 rounded-lg border border-border overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/40">
                <tr>
                  <th className="text-left px-4 py-3 text-muted-foreground font-medium">Item</th>
                  <th className="text-right px-4 py-3 text-muted-foreground font-medium">Qty</th>
                  <th className="text-right px-4 py-3 text-muted-foreground font-medium">Rate</th>
                  <th className="text-right px-4 py-3 text-muted-foreground font-medium">Amount</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => (
                  <tr key={it.id} className="border-t border-border">
                    <td className="px-4 py-3">{it.description}</td>
                    <td className="px-4 py-3 text-right">{Number(it.quantity || 0)}</td>
                    <td className="px-4 py-3 text-right">{money(inv.currency, Number(it.unit_price || 0))}</td>
                    <td className="px-4 py-3 text-right">{money(inv.currency, Number(it.quantity || 0) * Number(it.unit_price || 0))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-6 flex items-start justify-between gap-6">
            <div className="flex-1">
              {inv.notes ? (
                <>
                  <p className="text-xs uppercase text-muted-foreground">Notes</p>
                  <p className="text-sm text-foreground mt-1 whitespace-pre-wrap">{inv.notes}</p>
                </>
              ) : null}
              {inv.terms ? (
                <>
                  <p className="text-xs uppercase text-muted-foreground mt-4">Terms</p>
                  <p className="text-sm text-foreground mt-1 whitespace-pre-wrap">{inv.terms}</p>
                </>
              ) : null}
            </div>
            <div className="w-56 rounded-lg border border-border bg-muted/20 p-4">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Subtotal</span>
                <span className="text-foreground font-medium">{money(inv.currency, subtotal)}</span>
              </div>
              {taxes !== 0 ? (
                <div className="mt-2 flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Taxes</span>
                  <span className="text-foreground font-medium">{money(inv.currency, taxes)}</span>
                </div>
              ) : null}
              {discounts !== 0 ? (
                <div className="mt-2 flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Discounts</span>
                  <span className="text-foreground font-medium">-{money(inv.currency, discounts)}</span>
                </div>
              ) : null}
              {(adjustments || []).length ? (
                <div className="mt-3 pt-3 border-t border-border space-y-1 text-xs text-muted-foreground">
                  {(adjustments || []).map((a: any, idx: number) => {
                    const computed = computeAdjustmentAmount(a, subtotal);
                    const affectsTotal = !(a.type === "tax" && a.included_in_prices);
                    return (
                      <div key={idx} className="flex items-center justify-between gap-2">
                        <span className="truncate">
                          {a.label}
                          {a.type === "tax" && a.included_in_prices ? " (incl.)" : ""}
                        </span>
                        <span className="whitespace-nowrap">
                          {a.type === "discount" ? "-" : affectsTotal ? "" : ""}
                          {money(inv.currency, computed)}
                        </span>
                      </div>
                    );
                  })}
                </div>
              ) : null}
              <div className="mt-3 pt-3 border-t border-border flex items-center justify-between">
                <span className="text-sm font-semibold text-foreground">Total</span>
                <span className="text-sm font-semibold text-foreground">{money(inv.currency, total)}</span>
              </div>
              {inv.payment_terms ? (
                <p className="text-xs text-muted-foreground mt-3">Terms: {inv.payment_terms}</p>
              ) : null}
            </div>
          </div>

          <div className="mt-6 text-xs text-muted-foreground">
            Generated {format(new Date(), "MMM d, yyyy h:mm a")}
          </div>
        </div>
      </div>
    </div>
  );
}


